import { Flex, Text, Box, IconButton } from "@chakra-ui/react";
import { RImage, RModal } from "components";
import { 
  Icon2CarsSg, 
  IconCheckeredSg, 
  IconDoubleArrow,  
  IconEmptySet,
  IconGiftSg,
  IconGoldenTicket,
  IconStatsUpgrade, 
  IconTicketSg, 
  IconTrophyTournament, 
  IconUpgrade, 
  IconUpgradeNew, 
  IconWarningSg 
} from "icons";
import IconTicket from 'components/IconTicket/IconTicket';
import { conditionsConfig } from "components/Conditions/conditions.config";
import { useState, useCallback } from "react";
import ImpactTag from "components/ImpactTag/ImpactTag";
import RaceLevel from "components/Races2/RaceLevel/RaceLevel";
import { raceLevelGradient, rarityGradients } from "utils/themeConstants";
import CheckedTag from "components/CheckedTag/CheckedTag";
import IconSilverTicket from "components/IconSilverTicket/IconSilverTicket";


// Define a type for carousel slides
interface SlideData {
  levelRequirements: number[];
  raceLevel: number;
  stats: string[];
}

interface RaceLevelsModalProps {
  isOpen: boolean;
  onClose: () => void;
  slides?: SlideData[];
}

const defaultSlides: SlideData[] = [
  {
    levelRequirements: [1, 2, 3, 4, 5, 6],
    raceLevel: 1,
    stats: ["Speed", "Fast Turns"],
  },
  {
    levelRequirements: [1, 2, 3, 4, 5, 6],
    raceLevel: 2,
    stats: ["Speed", "Fast Turns"],
  },
  {
    levelRequirements: [3, 4, 5, 6],
    raceLevel: 3,
    stats: ["Speed", "Fast Turns", "Handling"],
  },
  {
    levelRequirements: [4, 5, 6],
    raceLevel: 4,
    stats: ["Speed", "Fast Turns", "Handling", "Slow Turns"],
  },
  {
    levelRequirements: [5, 6],
    raceLevel: 5,
    stats: ["Speed", "Fast Turns", "Handling", "Slow Turns", "Cooling", "Braking"],
  },
  {   
    levelRequirements: [6],
    raceLevel: 6,
    stats: ["Speed", "Fast Turns", "Handling", "Slow Turns", "Cooling", "Braking", "Acceleration", "Stability"],
  },
  {
    levelRequirements: [1, 2, 3, 4, 5, 6],
    raceLevel: 7,
    stats: ["Speed", "Fast Turns", "Handling", "Slow Turns", "Cooling", "Braking", "Acceleration", "Stability"],
  } 
  
]

const RaceLevelsModal = ({ 
  isOpen, 
  onClose,
}: RaceLevelsModalProps) => {
  const [slides] = useState<SlideData[]>(defaultSlides);
  const [currentSlide, setCurrentSlide] = useState(0);

  const handlePrevious = useCallback(() => {
    setCurrentSlide((prev) => (prev === 0 ? slides.length - 1 : prev - 1));
  }, [slides.length]);

  const handleNext = useCallback(() => {
    setCurrentSlide((prev) => (prev === slides.length - 1 ? 0 : prev + 1));
  }, [slides.length]);

  const goToSlide = useCallback((index: number) => {
    setCurrentSlide(index);
  }, []);

  const currentCondition = slides[currentSlide];

  return (
    <RModal
      isOpen={isOpen}
      onClose={onClose}
      size="2xl"
      title="RACE LEVELS"
      icon={<IconUpgradeNew />}
    >
      <Flex
        flexDir="column"
        gap="4"
      >
        <Text
          fontSize={{ base: '0.75rem', md: '0.875rem'}}
          lineHeight={{ base: '0.875rem', md: '1.125rem'}}
          fontWeight={500}
          color="white.80"
        >
          Race Levels determine which cars can enter, how rewards are distributed, the entry cost, and which stats you can adjust in Car Setup.
        </Text>

        {/* Carousel slide */}
        <Flex
          position="relative"
          w="full"
          borderRadius="0.5rem"
          background="linear-gradient(90deg, rgba(0, 0, 0, 0.6) 0%, rgba(0, 0, 0, 0) 100%)"
          p="1rem"
          _before={{
            content: '""',
            position: 'absolute',
            inset: 0,
            background: 'linear-gradient(90deg, rgba(79, 197, 186, 0.6) 0%, rgba(79, 197, 186, 0.06) 100%)',
            borderRadius: '0.5rem',
            padding: '2px',
            mask: "linear-gradient(#000 0 0) content-box, linear-gradient(#000 0 0)",
            maskComposite: "exclude",
            zIndex: -1,
          }}
          flexDir="column"
          gap="0.5"
        >
          <Flex alignItems="center" gap="0.25rem" mb="0.5rem">
            {currentSlide !== 6 ? (
              <RaceLevel level={currentCondition.raceLevel} isRaceLevel />
            ) : (
              <Flex
                flexDirection={{ base: 'column', md: 'row' }}
                alignItems="center"
                gap={4}
                zIndex={2}
                background="linear-gradient(225deg, #FFFFFF 0%, #EEEEEE 18%, #A0A0A0 73%, #979797 100%)" 
                borderTopRightRadius="12px"
                borderTopLeftRadius="2px"
                borderBottomLeftRadius="12px"
                borderBottomRightRadius="2px"
                px="2"
                py="1.5"
              >
                <IconTrophyTournament
                  width="16px"
                  height="16px"
                />
              </Flex>
            )}
          </Flex>

          <Flex
            background="linear-gradient(90deg, rgba(255, 255, 255, 0.05) 0%, rgba(255, 255, 255, 0) 100%)"
            py="0.5rem"
            px="0.75rem"
            w="full"
            borderRadius="0.25rem"
            gap="2"
            alignItems="center"
            justifyContent="space-between"
          >
            <Flex alignItems="center" gap="2" minW="fit-content">
              <Icon2CarsSg />

              <Text
                fontSize={{ base: '0.875rem', md: '1.125rem'}}
                lineHeight={{ base: '1.125rem', md: '1.5rem'}}
                fontWeight={500}
                color="white.80"
              >
                Level Requirement
              </Text>
            </Flex>

            <Flex 
              gap="2" 
              alignItems="center" 
              flexWrap="wrap"
              justifyContent="flex-end"
              maxW="200px"
            >
              {currentCondition.levelRequirements.map((level, index) => (
                <RaceLevel key={`level-${index}`} level={level} isRaceLevel />
              ))}
            </Flex>
          </Flex>

          <Flex
            background="linear-gradient(90deg, rgba(255, 255, 255, 0.05) 0%, rgba(255, 255, 255, 0) 100%)"
            py="0.5rem"
            px="0.75rem"
            w="full"
            borderRadius="0.25rem"
            gap="2"
            alignItems="center"
            justifyContent="space-between"
          >
            <Flex alignItems="center" gap="2">
              {currentSlide === 0 ? (
                <IconWarningSg />
              ) : (
                <IconTicketSg />
              )}

              <Text
                fontSize={{ base: '0.875rem', md: '1.125rem'}}
                lineHeight={{ base: '1.125rem', md: '1.5rem'}}
                fontWeight={500}
                color="white.80"
              >
                {currentSlide === 0 ? 'Restriction' : 'Entry'}
              </Text>
            </Flex>

            <Flex gap="2">
              {currentSlide === 0 ? (
                <Text
                  fontSize={{ base: '0.875rem', md: '1.125rem'}}
                  lineHeight={{ base: '1.125rem', md: '1.5rem'}}
                  fontWeight={500}
                  color="white.80"
                >
                  Less then 20 races
                </Text>
              ) : 
              currentSlide === 6 ? (
                <Flex>
                  <IconSilverTicket />
                  <IconGoldenTicket 
                    w="2.2rem"
                    h="2.2rem"
                  />
                </Flex>
              ) : (
                <IconTicket />
              )}
            </Flex>
          </Flex>

          <Flex
            background="linear-gradient(90deg, rgba(255, 255, 255, 0.05) 0%, rgba(255, 255, 255, 0) 100%)"
            p="0.5rem"
            w="full"
            borderRadius="0.25rem"
            justifyContent="space-between"
            alignItems="center"
            gap="3"
          >
            <Flex alignItems="center" gap="0.5rem" pl="0.25rem">
              {currentSlide === 0 ? (
                <IconTicketSg />
              ) : (
                <IconGiftSg />
              )}

              <Text
                fontSize={{ base: '0.875rem', md: '1.125rem'}}
                lineHeight={{ base: '1.125rem', md: '1.5rem'}}
                fontWeight={600}
                color="white.80"
              >
                {currentSlide === 0 ? 'Entry' : 'Rewards'}
              </Text>
            </Flex>

            <Flex pl="0.25rem" flexWrap="wrap" gap="2">
              {currentSlide === 0 ? (
                <Text
                  fontSize={{ base: '0.875rem', md: '1.125rem'}}
                  lineHeight={{ base: '1.125rem', md: '1.5rem'}}
                  fontWeight={500}
                  color="white.80"
                >
                  FREE
                </Text>
              ) : (
                <Flex alignItems="center" gap="0.5rem">
                  {currentSlide !== 6 && (
                    <>
                      <Text
                        fontSize={{ base: '0.875rem', md: '1.125rem'}}
                        lineHeight={{ base: '1.125rem', md: '1.5rem'}}
                        fontWeight={500}
                        color="bloodMoon.100"
                      >
                        Points
                      </Text>
                      <Text>
                        +
                      </Text>
                    </>
                  )}
                  <Text
                    fontSize={{ base: '0.875rem', md: '1.125rem'}}
                    lineHeight={{ base: '1.125rem', md: '1.5rem'}}
                    fontWeight={500}
                    color="usdc.100"
                  >
                    USDC
                  </Text>
                </Flex>
              )}
            </Flex>
          </Flex>

          <Flex>

          <Flex
            background="linear-gradient(90deg, rgba(255, 255, 255, 0.05) 0%, rgba(255, 255, 255, 0) 100%)"
            p="0.5rem"
            w="full"
            borderRadius="0.25rem"
            justifyContent="space-between"
            flexDir="column"
            gap="3"
          >
            <Flex alignItems="center" gap="0.5rem" pl="0.25rem">
              <IconStatsUpgrade />

              <Text
                fontSize={{ base: '0.875rem', md: '1.125rem'}}
                lineHeight={{ base: '1.125rem', md: '1.5rem'}}
                fontWeight={600}
                color="white.80"
              >
                Car Setups
              </Text>
            </Flex>

            <Flex pl="0.25rem" flexWrap="wrap" gap="2">
              {slides[currentSlide].stats.map((stat, index) => (
                <CheckedTag title={stat} key={`stat-${index}`} />
              ))}
            </Flex>
          </Flex>
          </Flex>
        </Flex>
      </Flex>

        

        {/* Carousel pagination and navigation */}
        <Flex justify="center" align="center" mt="2" gap="2">
          {/* Previous button */}
          <IconButton
            aria-label="Previous slide"
            icon={<IconDoubleArrow transform="rotate(180deg)" />}
            onClick={handlePrevious}
            variant="unstyled"
            display="flex"
            isDisabled={currentSlide === 0}
            alignItems="center"
            justifyContent="center"
            background="linear-gradient(225deg, rgba(240, 240, 240, 0.1) 0%, rgba(197, 197, 197, 0.1) 18%, rgba(176, 176, 176, 0.1) 73%, rgba(126, 126, 126, 0.1) 100%)"
            p="3"
            position="relative"
            _hover={{
              background: 'rgba(240, 240, 240, 0.2)',
            }}
            transition="background 0.3s ease"
            _before={{
              content: '""',
              position: 'absolute',
              inset: 0,
              background: 'linear-gradient(360deg, rgba(240, 240, 240, 0.3) 0%, rgba(240, 240, 240, 0.12) 100%)',
              padding: '2px',
              borderRadius: '0.25rem',
              mask: "linear-gradient(#000 0 0) content-box, linear-gradient(#000 0 0)",
              maskComposite: "exclude",
            }}
            minW="auto"
            h="auto"
          />

          {/* Pagination dots */}
          <Flex gap="2">
            {slides.map((_, index) => (
              <Box
                key={`dot-${index}`}
                w="0.75rem"
                h="0.75rem"
                borderRadius="full"
                bg={currentSlide === index ? "white" : "rgba(255, 255, 255, 0.3)"}
                cursor="pointer"
                onClick={() => goToSlide(index)}
              />
            ))}
          </Flex>

          {/* Next button */}
          <IconButton
            aria-label="Next slide"
            icon={<IconDoubleArrow />}
            onClick={handleNext}
            variant="unstyled"
            display="flex"
            isDisabled={currentSlide === slides.length - 1}
            alignItems="center"
            justifyContent="center"
            background="linear-gradient(225deg, rgba(240, 240, 240, 0.1) 0%, rgba(197, 197, 197, 0.1) 18%, rgba(176, 176, 176, 0.1) 73%, rgba(126, 126, 126, 0.1) 100%)"
            p="3"
            position="relative"
            minW="auto"
            h="auto"
            _hover={{
              background: 'rgba(240, 240, 240, 0.2)',
            }}
            transition="background 0.3s ease"
            _before={{
              content: '""',
              position: 'absolute',
              inset: 0,
              background: 'linear-gradient(360deg, rgba(240, 240, 240, 0.3) 0%, rgba(240, 240, 240, 0.12) 100%)',
              padding: '2px',
              borderRadius: '0.25rem',
              mask: "linear-gradient(#000 0 0) content-box, linear-gradient(#000 0 0)",
              maskComposite: "exclude",
            }}
          />
        </Flex>

    </RModal>
  );
};

export default RaceLevelsModal;